import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useAdmin } from '../hooks/useAdmin';

/**
 * Admin page. Shows reports (owners, sales, platform profit) and (in dev) Clerk user ID.
 */
export default function Admin() {
  const { getToken } = useAuth();
  const { isAdmin, loading: checking } = useAdmin();
  const [adminMe, setAdminMe] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const [meRes, reportsRes] = await Promise.all([
          api('/api/admin/me', { getToken }),
          api('/api/admin/reports', { getToken }),
        ]);
        if (!cancelled) {
          if (meRes.ok) setAdminMe(await meRes.json());
          if (reportsRes.ok) setReports(await reportsRes.json());
        }
      } catch {
        if (!cancelled) setAdminMe(null);
        if (!cancelled) setReports(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, getToken]);

  if (checking) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Checking permissions…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200 mb-2">Access denied</h2>
        <p className="text-slate-600 dark:text-slate-400">
          You don't have permission to view this page. Set Admin:ClerkUserId in appsettings to your Clerk user ID.
        </p>
      </div>
    );
  }

  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="max-w-md mx-auto space-y-6">
      <h1 className="text-3xl font-semibold">Admin</h1>

      {reports && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Reports</h2>
          <div className="grid gap-4">
            <div className="flex justify-between items-center py-2 border-b border-slate-200 dark:border-slate-600">
              <span className="text-slate-600 dark:text-slate-400">Total Owners</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">{reports.totalOwners}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-200 dark:border-slate-600">
              <span className="text-slate-600 dark:text-slate-400">Total Sales</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">{fmt(reports.totalSalesCents)}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600 dark:text-slate-400">Platform Profit (10%)</span>
              <span className="font-semibold text-amber-600 dark:text-amber-400">{fmt(reports.platformProfitCents)}</span>
            </div>
          </div>
        </div>
      )}

      {adminMe && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dev info</h2>
          <p className="text-slate-600 dark:text-slate-400"><strong>Clerk User ID:</strong> {adminMe.clerkUserId}</p>
          <p className="text-slate-600 dark:text-slate-400"><strong>Is Admin:</strong> {adminMe.isAdmin ? 'Yes' : 'No'}</p>
          {adminMe.hint && <p className="text-sm text-amber-700 dark:text-amber-300">{adminMe.hint}</p>}
        </div>
      )}
    </div>
  );
}
